/**
 * ContactListImportModal — a frameless modal-body that lets a user create a
 * contact list and import a CSV/XLSX into it without leaving the current page.
 * Rendered inside a WideModal (no page frame, no heading of its own).
 *
 * Flow: list name → file dropzone → phone-column confirm → "Create & import".
 * On success it surfaces the ImportResult and, once the user confirms, calls
 * `onCreated(listId, listName)` so the host can select the fresh list. The
 * logic mirrors the voice quick wizard's ContactsStep but shares nothing with
 * it (the voice module must not be imported from SMS surfaces).
 */

import { type DragEvent, type JSX, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Spinner } from '@/components/ui';
import {
  useCreateContactList,
  usePreviewImport,
  useRunImport,
  type ImportPreview,
  type ImportResult,
} from '@/hooks/useContactLists';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Inline dropzone — self-contained copy so this component shares nothing with
// the voice module. Mirrors the block in pages/contacts-import.tsx.
// ---------------------------------------------------------------------------
function Dropzone({
  onPick,
  busy,
  idle,
  reading,
  accept,
}: {
  onPick: (f: File) => void;
  busy: boolean;
  idle: string;
  reading: string;
  accept: string;
}): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  return (
    <div
      onClick={() => inputRef.current?.click()}
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setDragOver(false);
        const f = e.dataTransfer.files?.[0];
        if (f) onPick(f);
      }}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          inputRef.current?.click();
        }
      }}
      className={cn(
        'cursor-pointer rounded-md border-2 border-dashed py-10 px-6 text-center transition-colors',
        dragOver ? 'border-volt bg-volt-50' : 'border-line-strong bg-surface-2 hover:bg-surface',
      )}
    >
      <span
        aria-hidden="true"
        className="inline-flex w-12 h-12 rounded-full bg-volt-50 text-volt-600 grid place-items-center mb-3"
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path
            d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
      <div className="text-[14px] text-text font-medium mb-1">{busy ? reading : idle}</div>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onPick(f);
        }}
        className="hidden"
      />
    </div>
  );
}

const SELECT_CLS =
  'w-full h-10 px-3 text-sm bg-surface text-text border border-line-strong rounded-md outline-none focus:border-volt-500 focus:ring-[3px] focus:ring-volt-glow';

export function ContactListImportModal({
  onCreated,
}: {
  onCreated: (listId: number, listName: string) => void;
}): JSX.Element {
  const { t, i18n } = useTranslation('default');
  const lang = (i18n.resolvedLanguage ?? i18n.language ?? 'en').slice(0, 2);

  const createList = useCreateContactList();
  const preview = usePreviewImport();
  const runImport = useRunImport();

  const [listName, setListName] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [importPreview, setImportPreview] = useState<ImportPreview | null>(null);
  const [phoneColumn, setPhoneColumn] = useState<string>('');
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [createdList, setCreatedList] = useState<{ id: number; name: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function onPick(f: File) {
    setError(null);
    setImportResult(null);
    setFile(f);
    try {
      const p = await preview.mutateAsync(f);
      setImportPreview(p);
      setPhoneColumn(p.guessed_phone);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('cw_list_err_upload'));
      setImportPreview(null);
    }
  }

  async function doImport() {
    if (!file || !importPreview || listName.trim().length === 0 || phoneColumn.length === 0) return;
    setError(null);
    try {
      const list = await createList.mutateAsync({ name: listName.trim() });
      const res = await runImport.mutateAsync({ listID: list.id, file, phoneColumn });
      setImportResult(res);
      setCreatedList({ id: list.id, name: list.name });
    } catch (err) {
      setError(err instanceof Error ? err.message : t('cw_list_err_import'));
    }
  }

  const importing = createList.isPending || runImport.isPending;

  // Success view — surface the ImportResult, then let the user commit the list
  // to the host (which closes the modal via onCreated).
  if (importResult && createdList) {
    return (
      <div className="space-y-4">
        <div className="px-3.5 py-3 bg-volt-50 border border-volt-200 rounded-md text-[13px] text-volt-700">
          {t('cw_list_done', {
            inserted: importResult.inserted.toLocaleString(lang),
            skipped: importResult.skipped.toLocaleString(lang),
            invalid: importResult.invalid.toLocaleString(lang),
          })}
        </div>
        <div className="flex justify-end">
          <Button onClick={() => onCreated(createdList.id, createdList.name)}>
            {t('cw_list_use_btn')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-[12px] font-medium text-text-2 mb-1.5">
          {t('cw_list_name_label')}
        </label>
        <input
          value={listName}
          onChange={(e) => setListName(e.target.value)}
          placeholder={t('cw_list_name_ph')}
          className={SELECT_CLS}
        />
      </div>

      <Dropzone
        onPick={(f) => void onPick(f)}
        busy={preview.isPending}
        idle={t('cw_list_drop_idle')}
        reading={t('cw_list_drop_busy')}
        accept=".csv,.xlsx,.xls,.xlsm"
      />

      {importPreview && (
        <>
          <div className="px-3.5 py-2.5 bg-surface-2 border border-line rounded-md text-[13px] text-text-2">
            {t('cw_list_preview', {
              total: importPreview.total_rows.toLocaleString(lang),
            })}
          </div>
          <div>
            <label className="block text-[12px] font-medium text-text-2 mb-1.5">
              {t('cw_list_phone_col')}
            </label>
            <select
              value={phoneColumn}
              onChange={(e) => setPhoneColumn(e.target.value)}
              className={SELECT_CLS}
            >
              {importPreview.headers.map((h) => (
                <option key={h} value={h}>
                  {h}
                </option>
              ))}
            </select>
          </div>
        </>
      )}

      {error && (
        <div
          role="alert"
          className="px-3.5 py-3 bg-danger-bg border border-danger-bd rounded-md text-[13px] text-danger"
        >
          {error}
        </div>
      )}

      {importPreview && (
        <div className="flex justify-end">
          <Button
            onClick={() => void doImport()}
            disabled={importing || listName.trim().length === 0 || phoneColumn.length === 0}
          >
            {importing ? (
              <>
                <Spinner />
                <span>{t('cw_list_importing')}</span>
              </>
            ) : (
              t('cw_list_create_btn')
            )}
          </Button>
        </div>
      )}
    </div>
  );
}
